import { useEffect } from 'react';
import { useLocation } from '@tanstack/react-router';
import { ToastContainer, toast } from 'react-toastify';
import { GovBanner } from '@trussworks/react-uswds';
import AuthSessionWatcher from '@api/services/auth/AuthSessionWatcher';
import Modal from '@components/modal';
import SecondaryModal from '@components/secondary-modal';
import NavBar from '@components/navigation';
import LandingModal from '@pages/common/modals/Landing';
import RRSFooter from '@components/footer/Footer';
import EnvironmentVisibility, { currentEnvironment } from '@components/environment-visibility/EnvironmentVisibility';
import { useAuth, useUserProfile } from '@hooks';
import { useLookupTablesQuery } from './api/services/lookup/hooks';
import { useModal, useApp, useDeveloperStore } from '@stores';

import { DeveloperAccessQueries } from '@api/services/developer/query';
import { useQuery } from '@tanstack/react-query';
import Loader from './app-components/loader/Loader';

import '@trussworks/react-uswds/lib/uswds.css';
import '@trussworks/react-uswds/lib/index.css';
import 'react-toastify/dist/ReactToastify.css';
import '@styles/index.scss';
import '@styles/_buttons.scss';
import SupportDrawer from './app-components/support-drawer/SupportDrawer';
import { ProfileRoles } from '@src/utils/enums';
import ProfileModal from '@src/app-pages/Forms/components/modals/profile/Profile';

const App = ({ children }) => {
  const relativePathname = useLocation({
    select: (location) => location.pathname,
  });

  const { openModal } = useModal();
  const { isAuthenticated } = useAuth();
  const { userHasProfile, userProfileData, isLoading: isUserProfileLoading } = useUserProfile();
  const { setBannerStatus, setBannerText } = useDeveloperStore();
  const { isLoading, loadingText, setIsLoading } = useApp();

  const isInitializingAuthenticatedUser = isAuthenticated && isUserProfileLoading;
  const shouldLoadLookups = isAuthenticated && userHasProfile && !isInitializingAuthenticatedUser;
  const userRole = userProfileData?.role;

  useLookupTablesQuery(shouldLoadLookups);

  const requiresOrganization = userRole === ProfileRoles.USACERegulator || userRole === ProfileRoles.ExternalAgency;
  const isMissingOrganization = !userProfileData?.organizations?.length;
  const shouldOpenProfileForOrganization =
    isAuthenticated &&
    userHasProfile &&
    !isInitializingAuthenticatedUser &&
    requiresOrganization &&
    isMissingOrganization;

  useQuery({
    queryKey: ['bannerInfo'],
    queryFn: DeveloperAccessQueries.getBannerInfo,
    refetchInterval: 600000, // 10 minutes
    refetchIntervalInBackground: true,
    onSuccess: (data) => {
      setBannerText(data.banner);
      setBannerStatus(data.isBannerOn);
    },
  });

  useEffect(() => {
    if (isInitializingAuthenticatedUser || shouldOpenProfileForOrganization) {
      return;
    }

    if (!isAuthenticated || !userHasProfile) {
      const landingModalSeen = sessionStorage.getItem('landingModalSeen');

      if (!landingModalSeen || landingModalSeen === 'false') {
        openModal(LandingModal);
      }
    }
  }, [isAuthenticated, userHasProfile, isInitializingAuthenticatedUser, shouldOpenProfileForOrganization, openModal]);

  useEffect(() => {
    if (!shouldOpenProfileForOrganization) {
      return;
    }
    openModal(ProfileModal, { edit: true });
  }, [shouldOpenProfileForOrganization, openModal]);

  useEffect(() => {
    window.addEventListener('pageshow', (event) => {
      // Check if page was restored from the bfcache
      if (event.persisted) {
        // Clear loading overlay
        setIsLoading(false);
      }
    });
  }, [setIsLoading]);

  // Global handler to allow toast dismissal when modals are open
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (e.target.closest('.Toastify__close-button')) {
        e.stopImmediatePropagation();
        e.preventDefault();

        if (e.target.closest('.Toastify__toast')) {
          toast.dismiss();
        }
        return false;
      }
    };
    document.addEventListener('mousedown', handleMouseDown, true);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
    };
  }, []);

  return (
    <>
      <AuthSessionWatcher />
      <Loader loaderText={loadingText} showLoader={isLoading || isInitializingAuthenticatedUser} variant='app' />
      <ToastContainer autoClose={3500} hideProgressBar={false} style={{ zIndex: 999999999 }} />
      <EnvironmentVisibility mode='hide' prod>
        <div className={`environment-label ${currentEnvironment}`}>{currentEnvironment} environment</div>
      </EnvironmentVisibility>
      <div className='gov-banner-container'>
        <GovBanner language='english' tld='.mil' aria-label='Official government website' />
      </div>
      <NavBar className='margin-bottom-1' />
      <div className='page-container'>{isInitializingAuthenticatedUser ? null : children}</div>
      <SupportDrawer currentPath={relativePathname} userRole={userRole} />
      <RRSFooter />
      <Modal />
      <SecondaryModal />
    </>
  );
};
export default App;
